/**
 * CloudMaintenanceTable — shared table for Batteries, Communication Lost, and Other tabs
 * on the Cloud 5.0 alerts page.
 *
 * Columns: Detected, Duration, Condition, Device/Serial, Area, Resident
 *
 * No Clear button (unlike the 4.0 MaintenanceTable).
 * Data comes from `getdevicemaintenance` stored procedure, split by condition.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { durationToNow } from '../../../../libs.jsx';
import { getRoleWeight, getCampusTzOffset } from '../../../../services/session.js';
import { getAlertDeviceLink } from '../../../../utils/deviceLinks.js';
import { formatUtcForCampus } from '../../../../utils/campusTime.js';
import SortableTh from '../../../../components/v2/SortableTh.jsx';
import ResidentLinks from '../../../../components/v2/ResidentLinks.jsx';

function useTick(intervalMs = 1000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick((t) => t + 1), intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
}

const CloudMaintenanceTable = ({ data, sortColumn, sortOrder, onSort, tzOffset = getCampusTzOffset() }) => {
    useTick(1000);

    const sortIcon = (col) => {
        if (sortColumn !== col) return null;
        return <span style={{ marginLeft: 4, fontSize: 10 }}>{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
    };

    /* Shared with the Alerts table (utils/deviceLinks.js). This copy used to map only
       manufacturers 1 and 4, so a server-offline alert opened a PalCare device
       profile for the wrong record, and wandering devices never linked. */
    const getDeviceLink = (row) => getAlertDeviceLink(row, getRoleWeight());

    return (
        <div className="dp-table-wrap" style={{ overflowX: 'auto' }}>
            <table className="dp-table">
                <thead className="dp-thead">
                    <tr>
                        <SortableTh sortKey="event_datetime_utc" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Detected{sortIcon('event_datetime_utc')}</SortableTh>
                        <th className="dp-th">Duration</th>
                        <SortableTh sortKey="condition" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Condition{sortIcon('condition')}</SortableTh>
                        <th className="dp-th">Device / Serial</th>
                        <SortableTh sortKey="area_name" sortColumn={sortColumn} sortOrder={sortOrder} onSort={onSort} className="dp-th">Area{sortIcon('area_name')}</SortableTh>
                        <th className="dp-th">Resident</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={6}>
                                <div className="na-empty">
                                    <div className="na-empty__icon">{'\u2713'}</div>
                                    <div className="na-empty__text">No alerts in this category</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}
                    {data.map((row, idx) => {
                        const deviceLink = getDeviceLink(row);
                        return (
                            /* A device can have several problems, so device_id alone repeats. */
                            <tr key={row.device_id ? `${row.device_id}-${row.condition}` : idx} className="dp-tr">
                                <td className="dp-td" style={{ whiteSpace: 'nowrap' }}>{formatUtcForCampus(row.event_datetime_utc, tzOffset)}</td>
                                <td className="dp-td"><span className="na-duration">{durationToNow(row.event_datetime_utc)}</span></td>
                                <td className="dp-td">{row.condition || '\u2014'}</td>
                                <td className="dp-td">
                                    <div className="na-stack">
                                        {deviceLink ? (
                                            <Link to={deviceLink} className="ar-link">{row.device_name || '\u2014'}</Link>
                                        ) : (<span>{row.device_name || '\u2014'}</span>)}
                                        <span className="na-stack__sub">{row.manufacture_uuid || ''}</span>
                                    </div>
                                </td>
                                <td className="dp-td">
                                    {row.area_id ? (
                                        <Link to={`/profiles/area/${row.area_id}`} className="ar-link">{row.area_name || '\u2014'}</Link>
                                    ) : (row.area_name || '\u2014')}
                                </td>
                                <td className="dp-td">
                                    {/* Area devices list every resident in the area (mysql_fetch packDeviceResidents). */}
                                    <ResidentLinks residents={row.residents} residentId={row.resident_id} residentName={row.resident_name} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default CloudMaintenanceTable;
